import { Briefcase, Building, ChevronDown, Menu, Users } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Link, useLocation } from 'wouter';
import { AuthModal } from '../auth/auth-modal';
import { cn } from '@/lib/utils';

const mainlogo = '/mainLogo.svg';
const scrolledlogo = '/scrolledlogo.svg';

const HoverDropdown: React.FC<
  React.PropsWithChildren<{ trigger: React.ReactNode; className?: string }>
> = ({ trigger, className, children }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <div
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        style={{ display: 'inline-block' }}
      >
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent
          className={className ? `${className} z-[999]` : 'z-[999]'}
          align="start"
          sideOffset={8}
        >
          {children}
        </DropdownMenuContent>
      </div>
    </DropdownMenu>
  );
};

const agentsList = [
  {
    title: 'Bill Agent',
    description: 'Automatically identify and track relevant legislation.',
    href: '/products/bill-agent',
  },
  {
    title: 'Committee Agent',
    description: 'Get alerted to relevant committee hearings and events.',
    href: '/products/committee-agent',
  },
  {
    title: 'Voting Agent',
    description: 'Track voting records and patterns',
    comingSoon: true,
    href: '#',
  },
];

const searchPortalsList = [
  {
    title: 'Bills',
    description: 'Search and track legislation across all 50 states.',
    href: '/portals/bills',
  },
  {
    title: 'Members',
    description: 'Access profiles and voting records of legislators.',
    href: '/portals/members',
  },
  {
    title: 'Committees',
    description: 'Monitor committee activity and membership.',
    href: '/portals/committees',
  },
  {
    title: 'Transcript Hub',
    description: 'Live transcripts and video clips from hearings.',
    href: '/portals/transcript-hub',
  },
  {
    title: 'Lobby',
    description: 'Track lobbyists, firms, and company advocacy.',
    href: '/portals/lobby',
  },
  {
    title: 'Statutes & Regulations',
    description: 'Search state statutes and administrative regulations.',
    href: '/portals/statutes',
  },
  {
    title: 'Campaign Finance',
    description: 'Track political contributions and donor activity.',
    href: '/portals/campaign-finance',
  },
];

const toolsList = [
  {
    title: 'Agentic Track',
    description: 'Automatically identify and track relevant bills.',
    href: '/products/auto-track',
  },
  {
    title: 'Advocate Management',
    description: 'Manage lobbyist relationships and coverage.',
    href: '/advocate-management',
  },
  {
    title: 'AI Bill Brief',
    description: 'Generate impact reports with 30+ analysis options.',
    href: '/ai-reports',
  },
  {
    title: 'Bill Comparison',
    description: 'Compare bill text across versions and years.',
    href: '/products/bill-comparison',
  },
  {
    title: 'Live Alerts',
    description: 'Real-time notifications on bill changes and updates.',
    href: '/live-actions',
  },
];

function SiteHeader() {
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [location] = useLocation();
  const isLandingPage = location === '/';
  const isBlogPost = location.startsWith('/blog/post/');
  const [bannerHeight, setBannerHeight] = useState(0);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 5);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Detect promo banner height
  useEffect(() => {
    const checkBannerHeight = () => {
      const banner = document.getElementById('promo-banner');
      if (banner) {
        setBannerHeight(banner.offsetHeight);
      } else {
        setBannerHeight(0);
      }
    };

    checkBannerHeight();
    // Check periodically in case banner visibility changes
    const interval = setInterval(checkBannerHeight, 500);

    return () => clearInterval(interval);
  }, []);

  // Force white background on blog post pages
  const shouldBeWhite = scrolled || isBlogPost;

  return (
    <>
      <header
        className={`px-4 md:px-20 transition-all duration-300 h-20 z-[998] fixed left-0 right-0 overflow-x-hidden ${
          shouldBeWhite ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
        style={{ top: `${bannerHeight}px` }}
      >
        <div className="container mx-auto h-full px-4 overflow-x-hidden">
          <div className="relative flex items-center justify-between h-full">
            <Link href="/" className="flex items-center">
              <img
                src="/legisiq_blue_transparent.png"
                alt="LegisIQ Logo"
                className="h-auto w-auto"
                style={{ height: 'calc(2.25rem)' }}
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2 items-center space-x-8 xl:space-x-10">
              {/* Products Dropdown */}
              <HoverDropdown
                trigger={
                  <div
                    className={`flex items-center space-x-1 cursor-pointer text-base font-medium ${
                      shouldBeWhite
                        ? 'text-gray-700 hover:text-[#102A43]'
                        : 'text-gray-600 hover:text-[#102A43]'
                    } transition-colors`}
                  >
                    <span>Products</span>
                    <ChevronDown className="w-4 h-4" />
                  </div>
                }
                className="w-[900px]"
              >
                <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-8">
                  <div className="grid grid-cols-3 gap-8">
                    {/* Left Column - Agents */}
                    <div>
                      <h3 className="text-xs font-bold uppercase text-gray-500 mb-4">AI Agents</h3>
                      {agentsList.map((item, i) => (
                        <Link key={i} href={item.href}>
                          <div
                            className={`${i < agentsList.length - 1 ? 'mb-3' : ''} p-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer`}
                          >
                            <h4 className="font-bold text-gray-900 mb-1 text-sm">{item.title}</h4>
                            <p className="text-xs text-gray-600 leading-snug">
                              {item.description}
                              {item.comingSoon && <em className="text-gray-500"> (Coming Soon)</em>}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>

                    {/* Middle Column - Search Portals */}
                    <div>
                      <h3 className="text-xs font-bold uppercase text-gray-500 mb-4">
                        Search Portals
                      </h3>
                      {searchPortalsList.map((item, i) => (
                        <Link key={i} href={item.href}>
                          <div
                            className={`${i < searchPortalsList.length - 1 ? 'mb-3' : ''} p-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer`}
                          >
                            <h4 className="font-bold text-gray-900 mb-1 text-sm">{item.title}</h4>
                            <p className="text-xs text-gray-600 leading-snug">{item.description}</p>
                          </div>
                        </Link>
                      ))}
                    </div>

                    {/* Right Column - Tools & Reports */}
                    <div>
                      <h3 className="text-xs font-bold uppercase text-gray-500 mb-4">
                        Tools & Reports
                      </h3>
                      {toolsList.map((item, i) => (
                        <Link key={i} href={item.href}>
                          <div
                            className={`${i < toolsList.length - 1 ? 'mb-3' : ''} p-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer`}
                          >
                            <h4 className="font-bold text-gray-900 mb-1 text-sm">{item.title}</h4>
                            <p className="text-xs text-gray-600 leading-snug">{item.description}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </HoverDropdown>

              {/* Solutions Dropdown */}
              <HoverDropdown
                trigger={
                  <div
                    className={`flex items-center space-x-1 cursor-pointer text-base font-medium ${
                      shouldBeWhite
                        ? 'text-gray-700 hover:text-[#102A43]'
                        : 'text-gray-600 hover:text-[#102A43]'
                    } transition-colors`}
                  >
                    <span>Solutions</span>
                    <ChevronDown className="w-4 h-4" />
                  </div>
                }
                className="w-[360px]"
              >
                <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-6">
                  <h3 className="text-xs font-bold uppercase text-gray-500 mb-4">
                    Solutions by Organization Type
                  </h3>
                  <div className="space-y-2">
                    <Link href="/solutions/lobbyists">
                      <div className="p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer flex items-start gap-3">
                        <div className="mt-0.5">
                          <Briefcase className="w-4 h-4 text-gray-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 mb-1 text-sm">For Lobbyists</h4>
                          <p className="text-xs text-gray-600 leading-snug">
                            Tools for lobbying firms and advocacy professionals
                          </p>
                        </div>
                      </div>
                    </Link>
                    <Link href="/solutions/associations">
                      <div className="p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer flex items-start gap-3">
                        <div className="mt-0.5">
                          <Users className="w-4 h-4 text-gray-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 mb-1 text-sm">For Associations</h4>
                          <p className="text-xs text-gray-600 leading-snug">
                            Intelligence for trade associations and industry groups
                          </p>
                        </div>
                      </div>
                    </Link>
                    <Link href="/solutions/corporations">
                      <div className="p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer flex items-start gap-3">
                        <div className="mt-0.5">
                          <Building className="w-4 h-4 text-gray-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 mb-1 text-sm">For Corporations</h4>
                          <p className="text-xs text-gray-600 leading-snug">
                            Enterprise government affairs for in-house teams
                          </p>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>
              </HoverDropdown>

              <Link
                href="/why-legisiq"
                className={`text-base font-medium ${
                  shouldBeWhite
                    ? 'text-gray-700 hover:text-[#102A43]'
                    : 'text-gray-600 hover:text-[#102A43]'
                } transition-colors`}
              >
                Why LegisIQ
              </Link>
              <Link
                href="/about"
                className={`text-base font-medium ${
                  shouldBeWhite
                    ? 'text-gray-700 hover:text-[#102A43]'
                    : 'text-gray-600 hover:text-[#102A43]'
                } transition-colors`}
              >
                About
              </Link>
            </nav>

            {/* Desktop Buttons */}
            <div className="hidden lg:flex items-center space-x-4">
              <Button
                variant="outline"
                className="bg-white text-gray-900 hover:bg-gray-100 font-semibold px-8 py-3 rounded-xl transition-all duration-300"
                onClick={() => {
                  setLoginModalOpen(true);
                  setActiveTab('login');
                }}
              >
                Log in
              </Button>
              <a
                href="https://calendly.com/william-legisiq/30min"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-[#102A43] hover:bg-[#0d1f32] text-white font-semibold px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
                  Request Demo
                </Button>
              </a>
            </div>

            {/* Mobile Hamburger Button */}
            <button
              className={`lg:hidden p-2 ${shouldBeWhite ? 'text-[#102A43]' : 'text-gray-600'}`}
              onClick={toggleMobileMenu}
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation - OUTSIDE container for full width */}
      {mobileMenuOpen && (
        <div className="absolute left-0 right-0 top-20 w-full bg-white shadow-lg border-t border-gray-200 z-50">
          <div className="max-h-[calc(100vh-100px)] overflow-y-auto">
            <div className="p-4 space-y-1">
              {/* Products Section */}
              <div className="pb-2 mb-2 border-b border-gray-100">
                <p className="text-xs font-semibold uppercase text-gray-500 mb-2">AI Agents</p>
                {agentsList.map((item, i) => (
                  <Link key={i} href={item.href} onClick={() => setMobileMenuOpen(false)}>
                    <div className="block text-gray-700 rounded px-3 py-2 hover:bg-gray-50">
                      <div className="font-semibold text-sm">{item.title}</div>
                      <div className="text-xs text-gray-600">
                        {item.description}
                        {item.comingSoon && <em className="text-gray-500"> (Coming Soon)</em>}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="pb-2 mb-2 border-b border-gray-100">
                <p className="text-xs font-semibold uppercase text-gray-500 mb-2">Search Portals</p>
                {searchPortalsList.map((item, i) => (
                  <Link key={i} href={item.href} onClick={() => setMobileMenuOpen(false)}>
                    <div className="block text-gray-700 rounded px-3 py-2 hover:bg-gray-50">
                      <div className="font-semibold text-sm">{item.title}</div>
                      <div className="text-xs text-gray-600">{item.description}</div>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="pb-2 mb-2 border-b border-gray-100">
                <p className="text-xs font-semibold uppercase text-gray-500 mb-2">
                  Tools & Reports
                </p>
                {toolsList.map((item, i) => (
                  <Link key={i} href={item.href} onClick={() => setMobileMenuOpen(false)}>
                    <div className="block text-gray-700 rounded px-3 py-2 hover:bg-gray-50">
                      <div className="font-semibold text-sm">{item.title}</div>
                      <div className="text-xs text-gray-600">{item.description}</div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Solutions Section */}
              <div className="pb-2 mb-2 border-b border-gray-100">
                <p className="text-xs font-semibold uppercase text-gray-500 mb-2">Solutions</p>
                <Link href="/solutions/lobbyists" onClick={() => setMobileMenuOpen(false)}>
                  <div className="flex items-start gap-3 text-gray-700 rounded px-3 py-2 hover:bg-gray-50">
                    <Briefcase className="w-4 h-4 text-gray-600 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-semibold text-sm">For Lobbyists</div>
                      <div className="text-xs text-gray-600">
                        Tools for lobbying firms and advocacy professionals
                      </div>
                    </div>
                  </div>
                </Link>
                <Link href="/solutions/associations" onClick={() => setMobileMenuOpen(false)}>
                  <div className="flex items-start gap-3 text-gray-700 rounded px-3 py-2 hover:bg-gray-50">
                    <Users className="w-4 h-4 text-gray-600 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-semibold text-sm">For Associations</div>
                      <div className="text-xs text-gray-600">
                        Intelligence for trade associations and industry groups
                      </div>
                    </div>
                  </div>
                </Link>
                <Link href="/solutions/corporations" onClick={() => setMobileMenuOpen(false)}>
                  <div className="flex items-start gap-3 text-gray-700 rounded px-3 py-2 hover:bg-gray-50">
                    <Building className="w-4 h-4 text-gray-600 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-semibold text-sm">For Corporations</div>
                      <div className="text-xs text-gray-600">
                        Enterprise government affairs for in-house teams
                      </div>
                    </div>
                  </div>
                </Link>
              </div>

              {/* Other Links */}
              <Link
                href="/why-legisiq"
                className="block text-gray-700 hover:text-[#102A43] hover:bg-gray-50 rounded px-3 py-2 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Why LegisIQ
              </Link>
              <Link
                href="/about"
                className="block text-gray-700 hover:text-[#102A43] hover:bg-gray-50 rounded px-3 py-2 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </Link>
            </div>

            {/* Mobile Action Buttons */}
            <div className="p-4 bg-gray-50 border-t border-gray-200 space-y-3">
              <Button
                variant="outline"
                className="bg-white text-gray-900 hover:bg-gray-100 font-semibold w-full"
                onClick={() => {
                  setLoginModalOpen(true);
                  setActiveTab('login');
                  setMobileMenuOpen(false);
                }}
              >
                Log in
              </Button>
              <a
                href="https://calendly.com/william-legisiq/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Button className="bg-[#102A43] hover:bg-[#0d1f32] text-white font-semibold w-full">
                  Request Demo
                </Button>
              </a>
            </div>
          </div>
        </div>
      )}
      <AuthModal
        isOpen={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        defaultTab={activeTab}
      />
    </>
  );
}

export default SiteHeader;
